import "./UseCaseCards.scss";

import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { Chip, ChipBackgroundColor, ChipSize } from "@amzn/stencil-react-components/chip";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { IconBookmarkSmall } from "@amzn/stencil-react-icons";
import IconMessageSmall from "@amzn/stencil-react-icons/icons/icon-message-small";
import IconPeopleSmall from "@amzn/stencil-react-icons/icons/icon-people-small";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { UserAvatar } from "common/components/UserAvatar";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserUseCase } from "lib/models/userusecase-models";
import React, { FC, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

const DESCRIPTION_MAX_WORDS = 20;

interface UseCaseCardProps {
  useCase: Partial<UserUseCase>;
  toolNames: Record<string, string>;
  labelMap?: Record<string, string>;
  showBookmark?: boolean;
}

export const UseCaseCard: FC<UseCaseCardProps> = ({ useCase, toolNames, labelMap = {}, showBookmark = true }) => {
  const history = useHistory();
  const [isExpanded, setIsExpanded] = useState(false);
  const description = useCase.shortDescription || "";
  const words = useMemo(() => description.split(/\s+/).filter(Boolean), [description]);
  const needsTruncation = words.length > DESCRIPTION_MAX_WORDS;
  const truncated = words.slice(0, DESCRIPTION_MAX_WORDS).join(" ").trimEnd();

  const handleCardClick = () => {
    if (useCase.taxonomyId) {
      const slug = useCase.taxonomyId.replace(/^atn::/, "");
      history.push(`/aiforme/usecases/detail/${slug}`);
      window.scrollTo(0, 0);
    }
  };

  return (
    <Card
      className="use-case-card"
      style={{ padding: 0 }}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <Col gridGap="S200" className="use-case-card__content">
        <Row justifyContent="space-between" alignItems="flex-start">
          <Text fontSize="T300" fontWeight="bold" color="primary70" className="use-case-card__title">
            {useCase.name}
          </Text>
          {showBookmark && (
            <Button
              variant={ButtonVariant.Tertiary}
              icon={<IconBookmarkSmall />}
              aria-label="Bookmark"
              touchpoint="use-case-bookmark"
              className="use-case-card__bookmark"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </Row>

        <Text fontSize="T200" color="var(--st-dt-color-text-secondary)">
          {!needsTruncation ? (
            description
          ) : (
            <span>
              {isExpanded ? description : `${truncated}... `}
              <span
                className="use-case-card__toggle"
                role="button"
                tabIndex={0}
                style={{ color: "#0073bb", fontWeight: "bold" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }
                }}
              >
                {isExpanded
                  ? ` ${getString("useCases.contributeForm.showLess")}`
                  : getString("useCases.contributeForm.showMore")}
              </span>
            </span>
          )}
        </Text>

        <Row gridGap="S100" className="use-case-card__tags">
          {useCase.categories?.map((cat) => (
            <Chip key={cat} size={ChipSize.ExtraSmall} backgroundColor={ChipBackgroundColor.Purple}>
              {labelMap[cat] || cat}
            </Chip>
          ))}
          {useCase.associatedToolIds?.map((id) => (
            <Chip key={id} size={ChipSize.ExtraSmall}>
              {toolNames[id] ||
                id
                  .split("/")
                  .pop()
                  ?.replace(/-/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
            </Chip>
          ))}
        </Row>
      </Col>

      <Row className="use-case-card__footer" alignItems="center" justifyContent="space-between">
        <Row gridGap="S100" alignItems="center">
          <UserAvatar
            login={useCase.contributorAlias || useCase.contributor?.alias || ""}
            name={useCase.contributorFullName || useCase.contributorAlias || ""}
          />
        </Row>
        <Row gridGap="S200" alignItems="center">
          <Row gridGap="S100" alignItems="center">
            <IconMessageSmall aria-hidden />
            <Text fontSize="T100">{useCase.commentCount ?? 0}</Text>
          </Row>
          <Row gridGap="S100" alignItems="center">
            <IconPeopleSmall aria-hidden />
            <Text fontSize="T100">{useCase.usedCount ?? 0}</Text>
          </Row>
        </Row>
      </Row>
    </Card>
  );
};
